import { useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { useSubscribeToSignal, SignalTypes } from '@/contexts/useSignal';
import { useSessionContext } from '@/contexts/useSession';
import { getUserIdFromConnection } from '@/helpers/user';
import { pinnedByInstructorsAtom } from '@/recoil/atom/videoPinMirrorState';
import { customBugsnagLogger } from '@/utils/bugsnag';
import { applyPinSignal } from './applyPinSignal';

export { applyPinSignal };

const useReceiveVideoPins = () => {
	const setPinnedByInstructors = useSetRecoilState(pinnedByInstructorsAtom);
	const subscribeToVideoPinStatus = useSubscribeToSignal<{ isPinned: boolean }>(
		SignalTypes.VIDEO_PIN_STATUS
	);
	const { getSession } = useSessionContext();

	// Subscribe to signal updates
	useEffect(() => {
		const unsubscribe = subscribeToVideoPinStatus('*', (data, fromUserId) => {
			customBugsnagLogger.debug(
				`Received pin status from instructor ${fromUserId}: ${data.isPinned}`
			);
			setPinnedByInstructors((prev) =>
				applyPinSignal(prev, fromUserId, data.isPinned)
			);
		});
		return unsubscribe;
	}, [subscribeToVideoPinStatus, setPinnedByInstructors]);

	// Drop disconnected instructors from the atom
	useEffect(() => {
		const session = getSession();
		if (!session) return undefined;

		const handleConnectionDestroyed = ({ connection }: { connection: OT.Connection }) => {
			const userId = getUserIdFromConnection(connection);
			setPinnedByInstructors((prev) => {
				if (!prev.includes(userId)) return prev;
				customBugsnagLogger.debug(
					`Cleared instructor ${userId} from pinnedByInstructors on connectionDestroyed`
				);
				return prev.filter((id) => id !== userId);
			});
		};

		session.on('connectionDestroyed', handleConnectionDestroyed);
		return () => {
			session.off('connectionDestroyed', handleConnectionDestroyed);
		};
	}, [getSession, setPinnedByInstructors]);
};

export default useReceiveVideoPins;
